"use client"

import { useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import { LogIn } from "lucide-react"
import { useBranding } from "@/hooks/useBranding"
import { appTaglineForHost, DEFAULT_APP_TAGLINE } from "@/lib/app-tagline"
import { BrandLogo } from "@/components/brand-logo"
import { PublicThemeToggle } from "@/components/public-theme-toggle"
import {
  LS_USER,
  LS_LAST_ROLE,
  LS_HAS_SEEN_WELCOME,
  roleHome,
  nonTraderHome,
  isForceWelcome,
} from "@/lib/role-routes"

/**
 * Root page — the trader welcome screen, shown exactly ONCE per device.
 *
 * The routing decision now happens pre-paint in the inline guard in
 * app/layout.tsx (see ROOT_ROUTE_GUARD_SCRIPT), so a returning or logged-in
 * user never renders this component at all — no welcome flash. What remains
 * here is the slow path the guard cannot cover: recovering a trader whose
 * localStorage was evicted under Android storage pressure while the httpOnly
 * 7-day session cookie survived. That needs a network round-trip, so it can
 * only run after mount.
 */
export default function Home() {
  const router = useRouter()
  const { appName, ready } = useBranding()

  // Per-broker footer small print — seeded with the default so SSR/first
  // paint are unchanged for every broker. See lib/app-tagline.ts.
  const [tagline, setTagline] = useState(DEFAULT_APP_TAGLINE)
  useEffect(() => {
    setTagline(appTaglineForHost(window.location.hostname))
  }, [])

  useEffect(() => {
    // Wait for `ready`. Before branding resolves, `appName` is the build-time
    // NEXT_PUBLIC_APP_NAME — the BASE brand. Writing that into the tab title
    // flashes the platform's name at a white-label tenant, which is exactly
    // what a custom domain exists to prevent. A neutral title until we know
    // who this domain belongs to is the honest state.
    if (ready) document.title = appName || "Trading Platform"

    // Trader welcome lives at `/`, which inherits app/layout.tsx (admin
    // favicon default). Override both the favicon and apple-touch-icon at
    // runtime to the shared trader brand mark, matching the static metadata
    // that trade/layout.tsx ships for every /trade/* route. Pinned for every
    // tenant — no tenant `logoUrl` override.
    const setLink = (rel: string) => {
      let el: HTMLLinkElement | null = document.querySelector(`link[rel='${rel}']`)
      if (!el) { el = document.createElement("link"); el.rel = rel; document.head.appendChild(el) }
      el.href = "/default-pwa-logo.svg"
    }
    setLink("icon")
    setLink("apple-touch-icon")
  }, [appName, ready])

  useEffect(() => {
    // Mark as seen (keeps /trade/welcome backward-compat behaviour intact).
    // The pre-paint guard in app/layout.tsx already does this synchronously —
    // this is the belt for engines where the inline guard was skipped.
    localStorage.setItem(LS_HAS_SEEN_WELCOME, "true")

    // ?welcome=1 means "show me the welcome screen" — honour it and do nothing
    // else. Without this check the guard correctly stays on "/" and then this
    // effect redirects away a moment after hydration, which is the exact flash
    // we removed, only in reverse: the login back-button and the demo exit
    // would both bounce straight out of the screen they asked for.
    if (isForceWelcome(window.location.search)) return

    // If already logged in, forward to the right dashboard. In practice the
    // pre-paint guard has redirected long before this runs; this branch only
    // survives for engines where the inline guard was skipped. The route table
    // is shared so the two can't disagree.
    const userStr = localStorage.getItem(LS_USER)
    if (userStr) {
      try {
        const user = JSON.parse(userStr)
        if (user.role) localStorage.setItem(LS_LAST_ROLE, user.role)
        window.location.replace(roleHome(user.role))
        return
      } catch (_) {
        // Bad blob — strip it before falling through. Root-layout providers
        // (ws-provider, notification-provider) treat any truthy localStorage.user
        // as "authenticated" and would otherwise issue authed requests that 401
        // → session-expired handler → /admin/login (wrong tenancy from `/`).
        try { localStorage.removeItem(LS_USER) } catch (_) { /* private mode */ }
      }
    }

    // localStorage hint missing — could legitimately mean "never logged in"
    // (show welcome), but on mobile Chrome the localStorage bucket can be
    // evicted under storage pressure while the persistent 7-day session
    // cookie survives. Probe /api/auth/me to detect that case and recover
    // a trader session without making them re-login. Plain fetch so a 401
    // here doesn't trip the global session-expiry modal.
    //
    // A live NON-trader cookie is forwarded to that role's own home too. The
    // pre-paint guard normally catches those via `last_role`, but a device
    // whose localStorage was wiped wholesale has no hint left — and leaving a
    // logged-in admin parked on the trader welcome screen (one tap from the
    // trader login form) is the same lie their cookie already disproves.
    let cancelled = false
    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || ""
    const url = backendUrl
      ? `${backendUrl.replace(/\/$/, "")}/api/auth/me`
      : "/api/auth/me"
    fetch(url, { credentials: "include" })
      .then(async (res) => {
        if (cancelled || !res.ok) return
        const data = await res.json().catch(() => null)
        const u = data?.user
        if (!u) return
        if (u.role !== "trader") {
          const home = nonTraderHome(u.role)
          if (home) {
            localStorage.setItem(LS_LAST_ROLE, u.role)
            window.location.replace(home)
          }
          return
        }
        localStorage.setItem(
          LS_USER,
          JSON.stringify({
            id: u.id,
            username: u.username,
            name: u.name,
            fullname: u.name,
            role: u.role,
            enable_zerodha_ui: !!u.enable_zerodha_ui,
            is_demo: !!u.is_demo,
          })
        )
        localStorage.setItem(LS_LAST_ROLE, u.role)
        window.location.replace(roleHome(u.role))
      })
      .catch(() => { /* network error — stay on welcome */ })
    return () => { cancelled = true }
  }, [])

  return (
    <div className="flex min-h-[100dvh] w-full justify-center bg-background items-start md:items-center md:bg-[#050505]">
      <div className="relative flex h-[100dvh] w-full max-w-md flex-col bg-card-bg shadow-2xl md:h-[92vh] md:max-h-[850px] md:rounded-[3rem] md:border-[8px] md:border-[#222]" style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}>

        {/* Theme switcher — same corner the authed trader frame uses, so it
            does not move once the visitor logs in. */}
        <PublicThemeToggle />

        {/* Top spacer — sized to place the heading in the upper-middle of the
            screen. Ratio (top : heading→menu : menu→footer = 1.5 : 1 : 2.5)
            puts the heading top at ~20% from the viewport top. */}
        <div className="flex-[1.5]" />

        {/* Heading — logo always renders below the "Welcome to" text */}
        <div className="px-6 shrink-0">
          {!ready ? (
            <div className="flex flex-col items-start gap-2">
              <div className="h-9 w-[170px] rounded bg-wash animate-pulse" />
              <div className="h-6 w-[150px] rounded bg-wash animate-pulse" />
            </div>
          ) : (
            <h1 className="flex flex-col items-start gap-2 text-[32px] font-bold leading-[1.15] text-ink">
              <span>Welcome to</span>
              {/* The wordmark SVG's letters fill ~100% of its viewBox, so the
                  image's pixel height ≈ perceived letter height. 1em keeps the
                  logo proportional to the heading font-size without dwarfing it. */}
              <BrandLogo
                className="h-[1em] w-auto max-w-full object-contain object-left"
                fallback={<span>{appName}</span>}
              />
            </h1>
          )}
        </div>

        {/* Small spacer between heading and menu */}
        <div className="flex-1" />

        {/* Menu rows */}
        <div className="shrink-0">
          <div className="h-px bg-divider" />
          <button
            onClick={() => router.push("/trade/login")}
            className="w-full flex items-center justify-between px-6 py-[18px] active:bg-wash transition-colors"
          >
            {/* Brand name appended only once known — a custom-domain visitor
                must never read "Login to <base brand>". The row stays
                actionable in the meantime. */}
            <span className="text-[16px] text-ink font-medium">
              {ready ? `Login to ${appName}` : "Login"}
            </span>
            <LogIn className="h-5 w-5 text-ink-2" strokeWidth={1.5} />
          </button>
          <div className="h-px bg-divider" />
        </div>

        {/* Bottom spacer */}
        <div className="flex-[2.5]" />

        {/* Footer */}
        <div className="px-6 shrink-0" style={{ paddingBottom: "max(2rem, env(safe-area-inset-bottom, 2rem))" }}>
          <div className="flex items-center gap-1.5 mb-1.5">
            <svg width="10" height="12" viewBox="0 0 10 12" fill="none">
              <rect width="10" height="12" rx="1" fill="var(--color-ink-3)" />
            </svg>
            {/* Empty until branding resolves — never the base brand. Height is
                reserved by the line-height so the footer doesn't jump. */}
            <p className="text-[11px] font-bold uppercase tracking-widest text-ink-3">
              {ready ? appName : ""}
            </p>
          </div>
          <p className="text-[11px] text-ink-3 leading-[1.6]">{tagline}</p>
        </div>

      </div>
    </div>
  )
}
